import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

const ERROR_MESSAGES: Record<string, string> = {
  oauth_denied: 'Sign-in was cancelled.',
  missing_params: 'OAuth parameters missing. Please try again.',
  invalid_state: 'Invalid OAuth state. Please try again.',
  domain_not_allowed: 'Only @tddt.org Google accounts are allowed.',
  auth_failed: 'Authentication failed. Please try again.',
};

export const LoginPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const error = searchParams.get('error');
  const { error: authError, loading, refresh } = useAuth();
  const [meDebug, setMeDebug] = useState<string | null>(null);

  useEffect(() => {
    if (!loading) {
      fetch('/api/me', { credentials: 'include' })
        .then(r => r.text())
        .then(t => setMeDebug(t))
        .catch(e => setMeDebug(`fetch error: ${e.message}`));
    }
  }, [loading]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-sm p-8 text-center">
        {/* Logo / Brand */}
        <div className="mb-6">
          <div className="w-16 h-16 mx-auto mb-4 bg-blue-600 rounded-xl flex items-center justify-center">
            <span className="text-white text-2xl font-bold">R</span>
          </div>
          <h1 className="text-xl font-bold text-gray-900">Research Editor</h1>
          <p className="text-sm text-gray-500 mt-1">TDDT Research Documentation</p>
        </div>

        {error && ERROR_MESSAGES[error] && (
          <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
            {ERROR_MESSAGES[error]}
          </div>
        )}

        {authError && (
          <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
            Session check failed: {authError}
          </div>
        )}

        {meDebug && (
          <div className="mb-4 text-left">
            <div className="text-xs text-gray-500 font-medium mb-1">Session diagnostic (<a href="/api/debug" target="_blank" rel="noreferrer" className="text-blue-500 hover:underline">/api/debug</a>)</div>
            <pre className="px-3 py-2 bg-gray-100 rounded text-xs overflow-auto whitespace-pre-wrap break-all max-h-40">{meDebug}</pre>
            <button
              onClick={() => refresh()}
              className="mt-2 text-xs text-blue-600 hover:underline"
            >
              Retry session check
            </button>
          </div>
        )}

        <p className="text-sm text-gray-600 mb-6">
          Sign in with your <strong>@tddt.org</strong> Google account to access the
          documentation editor.
        </p>

        <a
          href="/api/auth/login"
          className="flex items-center justify-center gap-3 w-full py-2.5 px-4 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-colors shadow-sm"
        >
          <svg viewBox="0 0 24 24" className="w-5 h-5">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          Sign in with Google
        </a>

        <p className="mt-6 text-xs text-gray-400">
          Access is granted by an administrator after your first sign-in.
        </p>
      </div>
    </div>
  );
};
